/**
 * Worker-side Video System for Phase 10 - CORRECT OffscreenCanvas Implementation
 * 
 * CORRECT Approach:
 * 1. Host creates OffscreenCanvas (no context!)
 * 2. Host transfers OffscreenCanvas to worker
 * 3. Worker receives OffscreenCanvas and gets context
 * 4. Host sends ImageBitmap frames to worker
 * 5. Worker draws ImageBitmap frames to its OffscreenCanvas
 * 6. Worker performs all analysis and effects on OffscreenCanvas
 * 
 * This provides:
 * - True worker-side processing with full GPU access
 * - Zero-copy OffscreenCanvas ownership transfer
 * - Optimal performance for CV and artist effects
 * - Proper separation: host = coordination, worker = processing
 */
export class VideoSystem {
  // ✅ CORRECT: Worker-owned OffscreenCanvas (transferred from host)
  private offscreenCanvas: OffscreenCanvas | null = null;
  private ctx: OffscreenCanvasRenderingContext2D | null = null;
  private gl: WebGLRenderingContext | WebGL2RenderingContext | null = null;
  
  // Debug logging control
  private debugMode = false;

  /**
   * Enable or disable debug logging
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * Debug logging helper
   */
  private debugLog(message: string, ...args: any[]): void {
    if (this.debugMode) {
      console.log(message, ...args);
    }
  }
  
  // Frame processing configuration
  private targetFrameRate = 30; // Default target FPS for video processing
  private lastFrameTime = 0;
  private frameInterval = 1000 / this.targetFrameRate; // ms between frames
  
  // Processing state
  private hasLoggedFirstFrame = false;
  private frameCount = 0;
  
  // Video state for artist API
  private videoState = {
    isConnected: false,
    currentFrame: null as OffscreenCanvas | null,
    frameWidth: 0,
    frameHeight: 0,
    frameRate: 0,
    frameData: null as ImageData | null
  };
  
  // Phase 11 preparation - CV processing placeholder
  private cvFeatures = {
    faceDetection: false,
    handTracking: false,
    bodySegmentation: false
  };
  
  private cvResults = {
    faces: [] as any[],
    hands: [] as any[],
    bodySegmentation: null as any
  };

  constructor() {
    // VideoSystem ready for OffscreenCanvas processing
  }

  /**
   * Get the video API for inclusion in the viji object
   */
  public getVideoAPI() {
    return {
      isConnected: this.videoState.isConnected,
      currentFrame: this.videoState.currentFrame,
      frameWidth: this.videoState.frameWidth,
      frameHeight: this.videoState.frameHeight,
      frameRate: this.videoState.frameRate,
      getFrameData: () => this.videoState.frameData,
      faces: this.cvResults.faces,
      hands: this.cvResults.hands
    };
  }

  /**
   * ✅ CORRECT: Receive OffscreenCanvas transfer from host
   */
  public handleCanvasSetup(data: {
    offscreenCanvas: OffscreenCanvas;
    width: number;
    height: number;
    timestamp: number;
  }): void {
    try {
      // Clean up existing canvas
      this.disconnectVideo();
      
      // ✅ CORRECT: Receive transferred OffscreenCanvas
      this.offscreenCanvas = data.offscreenCanvas;
      
      // ✅ CORRECT: Get context on WORKER side (not host!)
      this.ctx = this.offscreenCanvas.getContext('2d', {
        willReadFrequently: true // Optimize for frequent getImageData calls
      });
      
      if (!this.ctx) {
        throw new Error('Failed to get 2D context from transferred OffscreenCanvas');
      }
      
      // Optional: Get WebGL context for advanced effects
      // Note: We can have multiple contexts on the same canvas
      try {
        this.gl = this.offscreenCanvas.getContext('webgl2') || this.offscreenCanvas.getContext('webgl');
      } catch (e) {
        // WebGL not available, continue with 2D
        this.debugLog('WebGL not available, using 2D context only');
      }
      
      // Update state
      this.videoState.isConnected = true;
      this.videoState.currentFrame = this.offscreenCanvas;
      this.videoState.frameWidth = data.width;
      this.videoState.frameHeight = data.height;
      this.frameCount = 0;
      this.hasLoggedFirstFrame = false;
      
      this.debugLog('✅ OffscreenCanvas received and setup completed (worker-side)', {
        width: data.width,
        height: data.height,
        hasWebGL: !!this.gl,
        targetFrameRate: this.targetFrameRate
      });
      
      this.debugLog('🎬 CORRECT OffscreenCanvas approach - Worker has full GPU access!');
      
    } catch (error) {
      console.error('Failed to setup OffscreenCanvas in worker:', error);
      this.disconnectVideo();
    }
  }

  /**
   * ✅ CORRECT: Receive ImageBitmap frame and draw to worker's OffscreenCanvas
   */
  public handleFrameUpdate(data: {
    imageBitmap: ImageBitmap;
    timestamp: number;
  }): void {
    if (!this.offscreenCanvas || !this.ctx) {
      console.warn('🔴 Received frame but OffscreenCanvas not setup');
      return;
    }
    
    try {
      // Log occasionally to avoid spam (very rarely now that it's working)
      if (this.frameCount % 150 === 0) { // Log every 150th frame (~5 seconds at 30fps)
        this.debugLog('✅ Worker received ImageBitmap frame:', {
          bitmapSize: `${data.imageBitmap.width}x${data.imageBitmap.height}`,
          canvasSize: `${this.offscreenCanvas.width}x${this.offscreenCanvas.height}`,
          frameCount: this.frameCount,
          timestamp: data.timestamp
        });
      }
      
      // ✅ CORRECT: Worker draws ImageBitmap to its OffscreenCanvas
      this.ctx.drawImage(data.imageBitmap, 0, 0, this.offscreenCanvas.width, this.offscreenCanvas.height);
      
      // Update frame processing
      this.processCurrentFrame(data.timestamp);
      
      // Close the transferred ImageBitmap to free memory
      data.imageBitmap.close();
      
      this.frameCount++;
      
    } catch (error) {
      console.error('🔴 Error processing video frame (worker-side):', error);
    }
  }

  /**
   * Process current frame (called when new frame is drawn)
   */
  private processCurrentFrame(timestamp: number): void {
    if (!this.offscreenCanvas || !this.ctx) {
      return;
    }
    
    try {
      // Get current frame data for analysis
      this.videoState.frameData = this.ctx.getImageData(
        0, 0, 
        this.offscreenCanvas.width, 
        this.offscreenCanvas.height
      );
      
      // Calculate frame rate based on frame arrival
      const deltaTime = timestamp - this.lastFrameTime;
      this.videoState.frameRate = deltaTime > 0 ? 1000 / deltaTime : 0;
      
      // Log first successful frame processing (once)
      if (!this.hasLoggedFirstFrame) {
              this.debugLog(`🎯 Worker-side OffscreenCanvas processing active: ${this.videoState.frameRate.toFixed(1)} FPS (${this.offscreenCanvas.width}x${this.offscreenCanvas.height})`);
      this.debugLog('✅ Full GPU access available for custom effects and CV analysis');
        this.hasLoggedFirstFrame = true;
      }
      
      // Phase 11 preparation - Perform CV analysis (placeholder)
      this.performCVAnalysis();
      
      this.lastFrameTime = timestamp;
    } catch (error) {
      console.error('Error processing video frame (worker-side):', error);
    }
  }

  /**
   * Handle video configuration updates (including disconnection and resize)
   */
  public handleVideoConfigUpdate(data: {
    targetFrameRate?: number;
    cvConfig?: any;
    width?: number;
    height?: number;
    disconnect?: boolean;
    timestamp: number;
  }): void {
    try {
      // Handle disconnection
      if (data.disconnect) {
        this.disconnectVideo();
        return;
      }
      
      // Handle resize
      if (data.width && data.height && this.offscreenCanvas) {
        this.resizeCanvas(data.width, data.height);
      }
      
      // Update processing configuration if provided
      if (data.targetFrameRate) {
        this.updateProcessingConfig(data.targetFrameRate);
      }
      
      // Phase 11 preparation - CV config
      if (data.cvConfig) {
        this.updateCVConfig(data.cvConfig);
      }
    } catch (error) {
      console.error('Error handling video config update:', error);
    }
  }

  /**
   * Resize the OffscreenCanvas (when video dimensions change)
   */
  private resizeCanvas(width: number, height: number): void {
    if (!this.offscreenCanvas) return;
    
    try {
      // Resize the OffscreenCanvas
      this.offscreenCanvas.width = width;
      this.offscreenCanvas.height = height;
      
      // Update state
      this.videoState.frameWidth = width;
      this.videoState.frameHeight = height;
      
      // Reset WebGL viewport if available
      if (this.gl) {
        this.gl.viewport(0, 0, width, height);
      }
      
      this.debugLog(`📐 OffscreenCanvas resized to ${width}x${height} (worker-side)`);
      
    } catch (error) {
      console.error('Error resizing OffscreenCanvas:', error);
    }
  }

  /**
   * Disconnect video and clean up resources
   */
  private disconnectVideo(): void {
    // ✅ Clear the OffscreenCanvas before disconnecting
    if (this.offscreenCanvas && this.ctx) {
      this.ctx.clearRect(0, 0, this.offscreenCanvas.width, this.offscreenCanvas.height);
      this.debugLog('🧹 Cleared OffscreenCanvas on disconnect');
    }
    
    // Clear canvas references
    this.offscreenCanvas = null;
    this.ctx = null;
    this.gl = null;
    
    // Update state
    this.videoState.isConnected = false;
    this.videoState.currentFrame = null;
    this.videoState.frameWidth = 0;
    this.videoState.frameHeight = 0;
    this.videoState.frameRate = 0;
    this.videoState.frameData = null;
    this.resetCVResults();
    this.hasLoggedFirstFrame = false;
    this.frameCount = 0;
    
    this.debugLog('Video disconnected (worker-side)');
  }

  /**
   * Update video processing configuration
   */
  private updateProcessingConfig(targetFrameRate: number): void {
    this.targetFrameRate = Math.max(1, Math.min(60, targetFrameRate)); // Clamp between 1-60 FPS
    this.frameInterval = 1000 / this.targetFrameRate;
    
    this.debugLog(`Video processing frame rate updated to ${this.targetFrameRate} FPS (worker-side)`);
  }

  /**
   * Phase 11 preparation - Update CV configuration
   */
  private updateCVConfig(cvConfig: any): void {
    // Placeholder for Phase 11 - Computer vision configuration
    this.cvFeatures = {
      faceDetection: cvConfig.faceDetection || false,
      handTracking: cvConfig.handTracking || false,
      bodySegmentation: cvConfig.bodySegmentation || false
    };
    
    this.debugLog('CV configuration updated (Phase 11 preparation, worker-side):', this.cvFeatures);
  }

  /**
   * Phase 11 preparation - Perform computer vision analysis
   */
  private performCVAnalysis(): void {
    // Placeholder for Phase 11 - Computer vision processing
    // This will include face detection, hand tracking, body segmentation
    
    if (this.cvFeatures.faceDetection) {
      // TODO Phase 11: Face detection using MediaPipe or TensorFlow.js
      // this.cvResults.faces = await detectFaces(this.videoState.frameData);
    }
    
    if (this.cvFeatures.handTracking) {
      // TODO Phase 11: Hand tracking using MediaPipe
      // this.cvResults.hands = await detectHands(this.videoState.frameData);
    }
    
    if (this.cvFeatures.bodySegmentation) {
      // TODO Phase 11: Body segmentation using TensorFlow.js
      // this.cvResults.bodySegmentation = await segmentBody(this.videoState.frameData);
    }
  }

  /**
   * Reset CV results
   */
  private resetCVResults(): void {
    this.cvResults = {
      faces: [],
      hands: [],
      bodySegmentation: null
    };
  }

  /**
   * Reset all video state (called when loading new scene)
   */
  public resetVideoState(): void {
    this.disconnectVideo();
    this.resetCVResults();
  }

  /**
   * Get current processing configuration
   */
  public getProcessingConfig(): { targetFrameRate: number; frameInterval: number; frameCount: number } {
    return {
      targetFrameRate: this.targetFrameRate,
      frameInterval: this.frameInterval,
      frameCount: this.frameCount
    };
  }

  /**
   * Get WebGL context for advanced effects (if available)
   */
  public getWebGLContext(): WebGLRenderingContext | WebGL2RenderingContext | null {
    return this.gl;
  }

  /**
   * ✅ WORKER API: Artists can access the OffscreenCanvas directly for custom effects
   */
  public getCanvasForArtistEffects(): OffscreenCanvas | null {
    return this.offscreenCanvas;
  }
} 